import { registerPlugin } from '@capacitor/core';

export interface BackgroundTokenRefreshPlugin {
  /**
   * Save Okta configuration for background token refresh (iOS & Android)
   */
  saveOktaConfig(options: {
    clientId: string;
    tokenUrl: string;
    redirectUri: string;
  }): Promise<void>;

  /**
   * Cancel all background token refresh tasks (iOS & Android)
   */
  cancelBackgroundRefresh(): Promise<void>;

  /**
   * Get refreshed tokens from Keychain (iOS only)
   * Returns tokens that were refreshed by the background task
   */
  getTokensFromKeychain(): Promise<{
    accessToken: string | null;
    idToken: string | null;
    refreshToken: string | null;
  }>;

  /**
   * Seed/refresh the iOS Keychain with the current Okta tokens (iOS only).
   * This is the "producer" half the iOS background refresh needs: the BGTask reads
   * `okta-refresh-token` from the Keychain, but only this call ever writes it from the foreground.
   * Tokens are passed as JSON strings (the Okta SDK token-object shape) so that both the BGTask's
   * parser and `getTokensFromKeychain()` consumers can read them back.
   */
  saveTokensToKeychain(options: {
    accessToken: string; // JSON.stringify of the Okta access-token object
    idToken: string;     // JSON.stringify of the Okta id-token object
    refreshToken: string; // JSON.stringify of the Okta refresh-token object
  }): Promise<void>;

  /**
   * Manually schedule a token refresh (iOS only)
   */
  scheduleRefresh(): Promise<void>;

  /**
   * Debug: Print all keychain tokens to Xcode console (iOS only)
   * Useful for testing/debugging to verify tokens are stored correctly
   */
  debugKeychainTokens(): Promise<{ message: string }>;

  /**
   * Save tokens to native storage (Android only)
   * Stores token objects for background WorkManager to use
   * Native code extracts token strings and expiry times from these objects
   */
  saveTokens(options: {
    accessToken: any; // Token object with accessToken, expiresAt, etc.
    idToken: any;     // Token object with idToken, expiresAt, etc.
    refreshToken: any; // Token object with refreshToken, expiresAt, etc.
  }): Promise<void>;

  /**
   * Get refreshed tokens from native storage (Android only)
   * Returns token objects that were refreshed by the background WorkManager
   * Note: Unlike iOS which returns JSON strings, Android returns parsed objects
   */
  getRefreshedTokens(): Promise<{
    accessToken: any | null; // Token object (already parsed by native code)
    idToken: any | null;     // Token object (already parsed by native code)
    refreshToken: any | null; // Token object (already parsed by native code)
  }>;

  /**
   * Save user profile to secure storage (iOS Keychain & Android EncryptedSharedPreferences)
   */
  saveUserProfile(options: {
    profile: string; // JSON stringified user profile
  }): Promise<void>;

  /**
   * Get user profile from secure storage (iOS Keychain & Android EncryptedSharedPreferences)
   */
  getUserProfile(): Promise<{
    profile: string | null; // JSON stringified user profile, or null if not found
  }>;

  /**
   * Cross-process refresh lock (iOS Keychain & Android EncryptedSharedPreferences).
   * Best-effort mutex shared with the native background refresh worker so the foreground and the
   * worker can't spend the same rotating refresh token at the same time. The lock auto-expires after
   * a TTL, so a crashed holder can't wedge refreshes permanently.
   * Returns { acquired: true } when the caller now holds the lock.
   */
  tryAcquireRefreshLock(): Promise<{ acquired: boolean }>;

  /**
   * Release the cross-process refresh lock taken by tryAcquireRefreshLock() (iOS & Android).
   */
  releaseRefreshLock(): Promise<void>;
}

const BackgroundTokenRefresh = registerPlugin<BackgroundTokenRefreshPlugin>('BackgroundTokenRefresh', {
  web: () => ({
    async saveOktaConfig() { console.warn('[BG] saveOktaConfig not available on web'); },
    async cancelBackgroundRefresh() { console.warn('[BG] cancelBackgroundRefresh not available on web'); },
    async getTokensFromKeychain() { return { accessToken: null, idToken: null, refreshToken: null }; },
    async saveTokensToKeychain() { console.warn('[BG] saveTokensToKeychain not available on web'); },
    async scheduleRefresh() { console.warn('[BG] scheduleRefresh not available on web'); },
    async saveTokens() { console.warn('[BG] saveTokens not available on web'); },
    async getRefreshedTokens() { return { accessToken: null, idToken: null, refreshToken: null }; },
    async debugKeychainTokens() { console.warn('[BG] debugKeychainTokens not available on web'); return { message: 'Not available on web' }; },
    async saveUserProfile() { console.warn('[BG] saveUserProfile not available on web'); },
    async getUserProfile() { return { profile: null }; },
    // Web has no second (background) process, so there's nothing to lock against — always "acquired".
    async tryAcquireRefreshLock() { return { acquired: true }; },
    async releaseRefreshLock() { /* no-op on web */ }
  })
});

export default BackgroundTokenRefresh;
